import * as IoC from 'typescript-ioc';
import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import { PagingViewModel } from '../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { IAccountsDAO } from '../../../api/accounts.dao';
import { IPaginationParams, ISortRequestParams, SortOrder } from '../../../api/shared.dto';
import { IRouter } from '../../../infrastructure/router';
import { RouteParams } from '../../../routes-table';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../infrastructure/sub-windows';
import { IEventDetailsDTO, IEventsDAO, TGetEventsSortKeys } from '../../../api/events.dao';
import { getEventNavigationLinks } from '../../../constants/navigation.constants';
import { LinkTargetValue, MINIMAL_SEARCH_LENGTH } from '../../../constants/common.constants';
import { IUsersDAO } from '../../../api/users.dao';
import { IAlertsService } from '../../../infrastructure/alerts';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { EventForm } from './_event-form/event-form.container';
import { IBlueprintsDAO, IEventBlueprintDTO } from '../../../api/blueprints/blueprints.dao';
import { hasPermission } from '../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../api/roles.dao';
import { getOrderBookingFormattedUrl } from '../../../infrastructure/utils/common.utils';

class EventsListViewModel {
    @mobx.observable
    private eventsList: IEventDetailsDTO[];

    @mobx.observable
    private eventIdToSubWindowMap: { [index: string] : ISubWindowFacade };

    private paginationVM: PagingViewModel;


    @mobx.observable
    public selectedFilterDeliveryTeam: string = '';

    @mobx.observable
    private sortField: TGetEventsSortKeys;

    @mobx.observable
    private sortOrder: SortOrder;

    @mobx.observable
    private isLoading: boolean;

    @mobx.observable
    public searchQuery = '';

    @mobx.observable
    public intl = null;

    @mobx.observable
    public selectedFilterCoordinators = '';

    @mobx.observable
    private eventBlueprints: {[key: string ]: IEventBlueprintDTO} = {};

    constructor(
        private readonly accountId: number,
        private readonly organizationName: string,
        public readonly anonymizationEnabled: boolean = false,
        public readonly orderBookingEnabled: boolean = false,
        intl: IntlShape,
        @IoC.Inject private readonly accountsDAO: IAccountsDAO = IoC.Container.get(IAccountsDAO),
        @IoC.Inject private readonly eventsDAO: IEventsDAO = IoC.Container.get(IEventsDAO),
        @IoC.Inject private readonly usersDAO: IUsersDAO = IoC.Container.get(IUsersDAO),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly routingService = IoC.Container.get<IRouter<RouteParams>>(IRouter),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly blueprintsDAO: IBlueprintsDAO = IoC.Container.get(IBlueprintsDAO)
    ) {
        this.eventIdToSubWindowMap = {};
        this.eventsList = [];
        this.paginationVM = new PagingViewModel();
        this.sortField = 'startDateTime';
        this.sortOrder = SortOrder.Desc;
        this.isLoading = false;
        this.intl = intl;
        mobx.reaction(this.paginationVM.getCurrentPage, this.fetchEventsList);
    }

    @mobx.action
    public async initialize(): Promise<void> {
        await Promise.all([
            // [OEP-1476] fetch Blueprints requires BLUEPRINTS_TAB permission
            hasPermission(Permission.BLUEPRINTS_TAB) && this.fetchEventBlueprints(),
            this.fetchEventsList()
        ]);
    }

    public getPagingVM() {
        return this.paginationVM;
    }

    public getAccountId() {
        return this.accountId;
    }

    public getEventsList() {
        return this.eventsList;
    }

    public isEventEditFormShown(eventId: number) {
        return this.eventIdToSubWindowMap[eventId]
            && this.eventIdToSubWindowMap[eventId].getStatus() !== SubWindowStatus.Closed;
    }

    @mobx.action
    public fetchEventsList = async () => {
        mobx.runInAction(() => {
            this.isLoading = true;
        });
        const requestParams: { accountId: number; deliveryTeamMembers?: string; coordinators?: string}
                            & IPaginationParams
                            & ISortRequestParams<TGetEventsSortKeys> = {
            accountId: this.accountId,
            size: this.paginationVM.getPageSize(),
            page: this.paginationVM.getCurrentPageForBE(),
            sortField: this.sortField,
            sortOrder: this.sortOrder,
            query:  this.searchQuery?.trim() || '',
            deliveryTeamMembers: this.selectedFilterDeliveryTeam || '',
        };
        if (this.selectedFilterCoordinators) {
            requestParams.coordinators = this.selectedFilterCoordinators;
        }
        const eventsFetchResult = await this.eventsDAO.getEvents(requestParams);
        this.setEventsList(eventsFetchResult.content);
        mobx.runInAction(() => {
            this.isLoading = false;
        });
        this.paginationVM.setTotalRecords(eventsFetchResult.totalElements);
    };

    @mobx.action
    public async fetchEventBlueprints(): Promise<void> {
        try {
            const response = await this.blueprintsDAO.getEventBlueprintList({ accountId: this.accountId });
            this.setEventBlueprints(response);
        } catch (error) {
            this.alertsService.alert({ message: this.errorParsingService.extractMessage(error) });
        }
    }

    public getBlueprintNameById(id: string): string | undefined {
        return this.eventBlueprints[id]?.name;
    }

    @mobx.action.bound
    private setEventBlueprints(blueprints: IEventBlueprintDTO[]): void {
        const _blueprints = {};

        blueprints.forEach(blueprint => {
            _blueprints[blueprint.blueprintId] = blueprint;
        });

        this.eventBlueprints = _blueprints;
    }

    @mobx.action
    public editEvent = (eventId: number) => {
        const existingSubWindowForThisEvent = this.eventIdToSubWindowMap[eventId];

        let shallOpenNewSubWindow = true;
        if (existingSubWindowForThisEvent && existingSubWindowForThisEvent.getStatus() !== SubWindowStatus.Closed) {
            existingSubWindowForThisEvent.maximize();
            shallOpenNewSubWindow = false;
        }

        if (shallOpenNewSubWindow) {
            this.eventIdToSubWindowMap[eventId] = this.subWindowService.showSubWindow({
                title: this.intl.formatMessage({
                    id: `oe.event.editEvent`,
                    description: 'Label text',
                    defaultMessage: 'Edit Event',
                }),
                componentToHost: EventForm,
                componentToHostProps: {
                    accountId: this.accountId,
                    eventId: eventId,
                    saveCompletedCallback: () => {
                        this.fetchEventsList();
                    },
                    deleteCompletedCallback: () => {
                        this.completeEventDeletion(eventId);
                    }
                },
            });
        }
    };

    @mobx.action
    public setSortParams = (sortField: TGetEventsSortKeys, sortOrder: SortOrder) => {
        this.sortField = sortField;
        this.sortOrder = sortOrder;
        this.fetchEventsList();
    };

    public getSortField = () => {
        return this.sortField;
    };

    public getSortOrder = () => {
        return this.sortOrder;
    };

    public getIsLoading() {
        return this.isLoading;
    }

    private closeEditForm(id: string) {
        this.eventIdToSubWindowMap[id].close();
    }

    private completeEventDeletion = (eventId: number) => {
        this.fetchEventsList();
        this.closeEditForm(eventId.toString());
    };

    public navigateToEventRoute = (eventId: number) => {
        const routeParams = {
            accountId: this.accountId.toString(),
            eventId: eventId.toString(),
        };
        const routesList = getEventNavigationLinks({ routeParams: routeParams}, this.intl);
        const routeEnum = routesList.find(route => !route.isHidden).routeEnum;
        this.routingService.navigate(routeEnum, routeParams);
    };

    public createEvent = () => {
        this.subWindowService.showSubWindow({
            title: this.intl.formatMessage({
                id: `oe.event.newEvent`,
                description: 'Label text',
                defaultMessage: '{prefix}New Event',
            }, {
                prefix: ''
            }),
            componentToHost: EventForm,
            componentToHostProps: {
                accountId: this.accountId,
                saveCompletedCallback: (eventId?: number) => {
                    this.navigateToEventRoute(eventId);
                },
            },
        });
    };

    public getAccountName(): string {
        return this.organizationName;
    }

    public onNewBookingClick = (): void => {
        window.open(getOrderBookingFormattedUrl(this.accountId), LinkTargetValue.BLANK);
    };

    @mobx.action
    private setEventsList(eventsList: IEventDetailsDTO[]) {
        this.eventsList = eventsList;
    }

    @mobx.action
    public setSearchQuery = (query: string) => {
        this.searchQuery = query;
        if (query.trim().length >= MINIMAL_SEARCH_LENGTH) {
            this.paginationVM.selectPage(1);
            this.fetchEventsList();
        }
    };

    @mobx.action
    public clearSearchQuery = () => {
        this.searchQuery = '';
        this.paginationVM.selectPage(1);
        this.fetchEventsList();
    };

    @mobx.action
    public fetchAccountCoordinatorsList = async (): Promise<{ id: string; label: string }[]> => {
        try {
            const response = await this.usersDAO.getAccountUsers({ accountId: this.accountId });
            return response.map(coordinator => ({
                id: coordinator.username,
                label: `${coordinator.firstName} ${coordinator.lastName}`
            }));
        } catch (error) {
            this.alertsService.alert({
                message: this.errorParsingService.extractMessage(error)
            });
        }
    };

    @mobx.action
    public handleChangeCoordinatorsFilter = (coordinatorsIds: string) => {
        if (this.selectedFilterCoordinators !== coordinatorsIds) {
            this.selectedFilterCoordinators = coordinatorsIds;
            this.paginationVM.selectPage(1);
            this.fetchEventsList();
        }
    };

    @mobx.action
    public handleChangeDeliveryTeamFilter = (deliveryTeamIds: string) => {
        this.selectedFilterDeliveryTeam = deliveryTeamIds;
        this.fetchEventsList();
    };

    public fetchFetchDeliveryTeamList = async (query: string): Promise<{ id: string; label: string }[]> => {
        const response = await this.usersDAO.searchDeliveryTeamMembers({
            page: 0,
            size: 100,
            query: query ? query.trim() : ''
        });
        return response.users.map(deliveryItem => (
            {id: deliveryItem.username, label: `${deliveryItem.firstName} ${deliveryItem.lastName}`}
        ));
    };
}

export { EventsListViewModel };
